/**
 * Client-side PDF export for Debate Engine debates.
 * Uses jsPDF — no server cost, no LLM tokens, no external API.
 *
 * Three flavors:
 *   - exportQuickPDF(discussion)        — vraag, synthese, verificatie (1-2 pages)
 *   - exportFullDebatePDF(discussion)   — alles incl. ronde 1, inbreng, ronde 2 (multi-page)
 *   - exportExecutiveSummaryPDF(d)      — vraag, conclusie, sterkste claims, inzichten, actiepunten
 */
import jsPDF from "jspdf";

const MARGIN = 15;
const PAGE_WIDTH = 210;       // A4 mm
const PAGE_HEIGHT = 297;      // A4 mm
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;
const LINE_HEIGHT = 5.5;

// -------- Helpers --------

const slugify = (str) =>
  (str || "debate")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 50);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("nl-NL", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return "";
  }
};

class PDFBuilder {
  constructor(title) {
    this.doc = new jsPDF({ unit: "mm", format: "a4" });
    this.y = MARGIN;
    this.pageNum = 1;
    this.title = title;
    this.drawHeader();
  }

  drawHeader() {
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(8);
    this.doc.setTextColor(120);
    this.doc.text("DEBATE ENGINE", MARGIN, 9);
    this.doc.setFont("helvetica", "normal");
    this.doc.text(`debate-engine.com   ·   pagina ${this.pageNum}`, PAGE_WIDTH - MARGIN, 9, { align: "right" });
    this.doc.setDrawColor(200);
    this.doc.line(MARGIN, 11, PAGE_WIDTH - MARGIN, 11);
    this.doc.setTextColor(0);
    this.y = MARGIN + 5;
  }

  newPage() {
    this.doc.addPage();
    this.pageNum += 1;
    this.drawHeader();
  }

  ensureSpace(needed) {
    if (this.y + needed > PAGE_HEIGHT - MARGIN) this.newPage();
  }

  h1(text) {
    this.ensureSpace(12);
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(16);
    const lines = this.doc.splitTextToSize(text, CONTENT_WIDTH);
    this.doc.text(lines, MARGIN, this.y);
    this.y += lines.length * 7 + 3;
  }

  h2(text) {
    this.ensureSpace(10);
    this.y += 2;
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(11);
    this.doc.setTextColor(45, 140, 240);
    this.doc.text(text.toUpperCase(), MARGIN, this.y);
    this.doc.setTextColor(0);
    this.y += 5;
    this.doc.setDrawColor(220);
    this.doc.line(MARGIN, this.y, PAGE_WIDTH - MARGIN, this.y);
    this.y += 4;
  }

  meta(text) {
    this.doc.setFont("helvetica", "italic");
    this.doc.setFontSize(9);
    this.doc.setTextColor(120);
    this.doc.text(text, MARGIN, this.y);
    this.doc.setTextColor(0);
    this.y += 6;
  }

  paragraph(text) {
    if (!text) return;
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10);
    const lines = this.doc.splitTextToSize(text, CONTENT_WIDTH);
    for (const line of lines) {
      this.ensureSpace(LINE_HEIGHT);
      this.doc.text(line, MARGIN, this.y);
      this.y += LINE_HEIGHT;
    }
    this.y += 2;
  }

  bullet(text) {
    if (!text) return;
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10);
    const lines = this.doc.splitTextToSize(text, CONTENT_WIDTH - 5);
    for (let i = 0; i < lines.length; i++) {
      this.ensureSpace(LINE_HEIGHT);
      if (i === 0) this.doc.text("•", MARGIN, this.y);
      this.doc.text(lines[i], MARGIN + 5, this.y);
      this.y += LINE_HEIGHT;
    }
  }

  label(text) {
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(8);
    this.doc.setTextColor(100);
    this.doc.text(text.toUpperCase(), MARGIN, this.y);
    this.doc.setTextColor(0);
    this.y += 4;
  }

  card(title, subtitle, body) {
    this.ensureSpace(20);
    const startY = this.y;
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(10);
    this.doc.text(title, MARGIN + 2, startY + 5);
    if (subtitle) {
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(8);
      this.doc.setTextColor(120);
      this.doc.text(subtitle, MARGIN + 2 + this.doc.getTextWidth(title) + 3, startY + 5);
      this.doc.setTextColor(0);
    }
    this.y = startY + 9;
    this.paragraph(body || "");
    this.doc.setDrawColor(210);
    this.doc.line(MARGIN, this.y, PAGE_WIDTH - MARGIN, this.y);
    this.y += 4;
  }

  footer() {
    this.doc.setFont("helvetica", "italic");
    this.doc.setFontSize(7);
    this.doc.setTextColor(150);
    this.doc.text(
      "Gegenereerd door Debate Engine — debate-engine.com",
      PAGE_WIDTH / 2, PAGE_HEIGHT - 8, { align: "center" }
    );
  }

  save(filename) {
    this.footer();
    this.doc.save(filename);
  }
}

// -------- Quick PDF: vraag, synthese, verificatie --------

export function exportQuickPDF(discussion) {
  const pdf = new PDFBuilder(discussion.question);
  pdf.h1(discussion.question);
  pdf.meta(
    `${discussion.debater_count || (discussion.round1?.length || 0)} debaters · ${formatDate(discussion.created_at)}`
  );

  if (discussion.synthesis) {
    pdf.h2("Synthese");
    pdf.paragraph(discussion.synthesis.work_conclusion);

    if (discussion.synthesis.strongest_surviving_claims?.length) {
      pdf.label("Sterkste claims");
      discussion.synthesis.strongest_surviving_claims.forEach((c) => pdf.bullet(c));
      pdf.y += 2;
    }
    if (discussion.synthesis.new_insight) {
      pdf.label("Nieuw inzicht");
      pdf.paragraph(discussion.synthesis.new_insight);
    }
    if (discussion.synthesis.further_questions?.length) {
      pdf.label("Vervolgvragen");
      discussion.synthesis.further_questions.forEach((q) => pdf.bullet(q));
      pdf.y += 2;
    }
    if (discussion.synthesis.confidence_level) {
      pdf.meta(`Betrouwbaarheid: ${discussion.synthesis.confidence_level}`);
    }
  }

  if (discussion.verification?.verified_claims?.length) {
    pdf.h2("Verificatie");
    discussion.verification.verified_claims.forEach((v) => {
      pdf.bullet(`[${v.status}] ${v.claim}${v.evidence ? ` — ${v.evidence}` : ""}`);
    });
    if (discussion.verification.overall_reliability) {
      pdf.y += 2;
      pdf.meta(`Overall reliability: ${discussion.verification.overall_reliability}`);
    }
  }

  pdf.save(`debate-quick-${slugify(discussion.question)}.pdf`);
}

// -------- Full Debate PDF: alles --------

export function exportFullDebatePDF(discussion) {
  const pdf = new PDFBuilder(discussion.question);
  pdf.h1(discussion.question);
  pdf.meta(
    `${discussion.debater_count || (discussion.round1?.length || 0)} debaters · ${formatDate(discussion.created_at)} · Status: ${discussion.status}`
  );

  // Ronde 1
  if (discussion.round1?.length) {
    pdf.h2("Ronde 1 — Eerste Analyse");
    discussion.round1.forEach((r) => {
      pdf.card(
        r.role_display || r.role,
        `via ${r.provider_display || r.provider}`,
        r.error || r.response || ""
      );
    });
  }

  // Menselijke inbreng
  if (discussion.human_input) {
    pdf.h2("Jouw Inbreng");
    pdf.paragraph(discussion.human_input);
  }

  // Ronde 2
  if (discussion.round2?.length) {
    pdf.h2("Ronde 2 — Verdieping");
    discussion.round2.forEach((r) => {
      pdf.card(
        r.role_display || r.role,
        `via ${r.provider_display || r.provider}`,
        r.error || r.response || ""
      );
    });
  }

  // Synthese
  if (discussion.synthesis) {
    pdf.h2("Synthese");
    pdf.paragraph(discussion.synthesis.work_conclusion);

    if (discussion.synthesis.strongest_surviving_claims?.length) {
      pdf.label("Sterkste claims");
      discussion.synthesis.strongest_surviving_claims.forEach((c) => pdf.bullet(c));
      pdf.y += 2;
    }
    if (discussion.synthesis.weakest_claims?.length) {
      pdf.label("Zwakste claims");
      discussion.synthesis.weakest_claims.forEach((c) => pdf.bullet(c));
      pdf.y += 2;
    }
    if (discussion.synthesis.key_tensions?.length) {
      pdf.label("Kernspanningen");
      discussion.synthesis.key_tensions.forEach((t) => pdf.bullet(t));
      pdf.y += 2;
    }
    if (discussion.synthesis.new_insight) {
      pdf.label("Nieuw inzicht");
      pdf.paragraph(discussion.synthesis.new_insight);
    }
    if (discussion.synthesis.further_questions?.length) {
      pdf.label("Vervolgvragen");
      discussion.synthesis.further_questions.forEach((q) => pdf.bullet(q));
      pdf.y += 2;
    }
    if (discussion.synthesis.confidence_level) {
      pdf.meta(`Betrouwbaarheid: ${discussion.synthesis.confidence_level}`);
    }
  }

  // Verificatie
  if (discussion.verification?.verified_claims?.length) {
    pdf.h2("Verificatie");
    discussion.verification.verified_claims.forEach((v) => {
      pdf.bullet(`[${v.status}] ${v.claim}${v.evidence ? ` — ${v.evidence}` : ""}`);
    });
    if (discussion.verification.overall_reliability) {
      pdf.y += 2;
      pdf.meta(`Overall reliability: ${discussion.verification.overall_reliability}`);
    }
  }

  pdf.save(`debate-full-${slugify(discussion.question)}.pdf`);
}

// -------- Executive Summary PDF: vraag, conclusie, sterkste, inzichten, actiepunten --------

export function exportExecutiveSummaryPDF(discussion) {
  const pdf = new PDFBuilder(discussion.question);
  pdf.h1("Executive Summary");
  pdf.meta(`Vraag: ${discussion.question}`);
  pdf.meta(`${formatDate(discussion.created_at)} · ${discussion.debater_count || (discussion.round1?.length || 0)} debaters`);
  pdf.y += 3;

  const s = discussion.synthesis || {};

  if (s.work_conclusion) {
    pdf.h2("Conclusie");
    pdf.paragraph(s.work_conclusion);
  }

  if (s.strongest_surviving_claims?.length) {
    pdf.h2("Sterkste Argumenten");
    s.strongest_surviving_claims.forEach((c) => pdf.bullet(c));
  }

  if (s.new_insight) {
    pdf.h2("Nieuw Inzicht");
    pdf.paragraph(s.new_insight);
  }

  if (s.further_questions?.length) {
    pdf.h2("Vervolgvragen");
    s.further_questions.forEach((q) => pdf.bullet(q));
  }

  // Actiepunten — afgeleid uit further_questions als simpele next-steps
  if (s.further_questions?.length) {
    pdf.h2("Actiepunten");
    s.further_questions.forEach((q) => pdf.bullet(`Onderzoek: ${q}`));
  }

  if (s.confidence_level) {
    pdf.y += 2;
    pdf.meta(`Betrouwbaarheid van de synthese: ${s.confidence_level}`);
  }

  pdf.save(`debate-executive-${slugify(discussion.question)}.pdf`);
}
